// Браузерный транспорт Engine A (§4). Это `judge` для selectQuotes в проде:
//   selectQuotes(stages, { carrierStages, judge: httpCallLLM })
//
// POST сырых полей этапа на /api/extract-quotes (Vercel-функция держит ключ и
// строит промпт). Возвращает verdict рубрики { fields:[{field,qualifies,phrase}] }.
//
// ⭐ ЭТО ЕДИНСТВЕННЫЙ СЛОЙ ПОВТОРОВ (`s889.d2`, строка 71, cc-213): функция и SDK не повторяют — один
// запрос = один вызов модели (`api/extract-quotes.js`, шапка). Худший случай: 3 вызова модели на этап.
//
// Устойчивость (решение Kyrylo #2): временные сбои (сеть/таймаут/5xx/429)
// повторяем — 2 повтора (3 попытки суммарно), растущая пауза. Постоянные
// (нет ключа/кривой запрос/прочие 4xx) повторять бессмысленно → бросаем сразу.
// Исчерпали повторы → бросаем TransportError; оркестратор (index.js) ловит и
// отдаёт документ без цитат, не роняя конвейер.

const ENDPOINT = '/api/extract-quotes';

const DEFAULT_MAX_RETRIES = 2; // 2 повтора → 3 попытки суммарно
const DEFAULT_BACKOFF_MS = 300; // растущая пауза: base·2^(n−1) → 300, 600 ms

/**
 * ВЕРХНЯЯ ГРАНИЦА ОДНОЙ ПОПЫТКИ (cc-31). До неё её не было вообще: `fetch` уходил без
 * `AbortSignal`, то есть длительность попытки не ограничивалась ничем — а значит и суммарное
 * окно ожидания тоже. Формула окна: `попыток × длительность + паузы`; без верхней границы у
 * первого множителя произведение не ограничено.
 *
 * Нашлось не здесь, а сбоку: CD спросил, сколько секунд экран визарда может молчать, пока идут
 * молчаливые повторы диктовки, — и оказалось, что ответить нечем. По цитатам бьёт сильнее, чем
 * по голосу: там зависший сервис держит сборку паспорта, и пожаловаться на экране некому.
 *
 * 30с на попытку — не «быстро», а ОГРАНИЧЕННО. Судья Engine A это LLM-вызов, он законно думает
 * секунды, и резать его агрессивно значит плодить ложные срывы на медленных ответах. Худший
 * случай теперь считается: 3 × 30 + 0.9 ≈ 91с и дальше документ отдаётся без цитат.
 *
 * Голос эту цифру НЕ наследует. Цитаты идут офлайн-прогоном, где никто не ждёт; диктовка — при
 * человеке посреди фразы, и бюджет там задаёт его терпение: 2 попытки × 800мс + 300мс ≈ 1.9с
 * (провизорно, до выбора транспорта распознавания). Поэтому таймаут инъектируем.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Сбой транспорта. `transient` — временный (сеть/таймаут/5xx/429), можно
 * повторять; иначе постоянный (нет ключа/кривой запрос/4xx) — повтор не поможет.
 */
export class TransportError extends Error {
  constructor(message, { transient = true, status = null, attempts = 1, cause } = {}) {
    super(message);
    this.name = 'TransportError';
    this.transient = Boolean(transient);
    this.status = status;
    this.attempts = attempts;
    if (cause) this.cause = cause;
  }
}

/** Временный сбой vs постоянный по HTTP-статусу (§4, решение #2). */
export function classifyHttpStatus(status) {
  if (status === 429) return 'transient'; // rate limit — переждать
  if (status >= 500) return 'transient'; // 5xx — сервер захлебнулся
  return 'permanent'; // 4xx (нет ключа / кривой запрос) — повтор бессмыслен
}

const realSleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Повтор с растущей паузой. Повторяет ТОЛЬКО transient-сбои до maxRetries раз;
 * постоянные пробрасывает сразу; исчерпанные transient — пробрасывает как
 * TransportError с числом попыток. sleep/backoffBase инъектируемы (быстрые тесты).
 *
 * @param {(attempt:number)=>Promise<any>} fn   одна попытка (бросает при сбое)
 * @param {object} [opts]
 * @param {number} [opts.maxRetries]            повторов сверх первой попытки
 * @param {number} [opts.backoffBase]           база паузы, ms
 * @param {(ms:number)=>Promise} [opts.sleep]   инъекция паузы
 * @param {(ev:object)=>void} [opts.onEvent]    лог событий повтора/сдачи
 */
export async function withRetry(
  fn,
  { maxRetries = DEFAULT_MAX_RETRIES, backoffBase = DEFAULT_BACKOFF_MS, sleep = realSleep, onEvent = () => {} } = {},
) {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    attempt += 1;
    try {
      return await fn(attempt);
    } catch (err) {
      const isTransport = err instanceof TransportError;
      const transient = isTransport ? err.transient : true; // сетевой throw/abort → временный
      if (!transient) {
        onEvent({ event: 'transport_permanent', attempt, status: err.status ?? null });
        throw err;
      }
      if (attempt > maxRetries) {
        const finalErr = isTransport
          ? err
          : new TransportError(err.message || 'transport error', { transient: true, cause: err });
        finalErr.attempts = attempt;
        onEvent({ event: 'transport_giveup', attempts: attempt, status: finalErr.status ?? null });
        throw finalErr;
      }
      onEvent({ event: 'transport_retry', attempt, status: isTransport ? err.status ?? null : null });
      await sleep(backoffBase * 2 ** (attempt - 1));
    }
  }
}

/**
 * @param {object} fields  { T1,T2,T3 } одного этапа
 * @param {{isCarrier?:boolean}} [ctx]
 * @param {object} [opts]  retry-настройки (см. withRetry) + fetchImpl (инъекция)
 * @returns {Promise<{fields:Array}>}
 */
export async function httpCallLLM(fields, { isCarrier = false } = {}, opts = {}) {
  const { fetchImpl = globalThis.fetch, timeoutMs = DEFAULT_TIMEOUT_MS, ...retryOpts } = opts;
  return withRetry(async () => {
    let resp;
    // ГРАНИЦА ДЕРЖИТСЯ ДВУМЯ РАЗНЫМИ ВЕЩАМИ, и это не дублирование.
    //   · `signal` — чтобы запрос был ДЕЙСТВИТЕЛЬНО отменён: соединение закрылось, сервер
    //     перестал считать, браузер освободил слот. Это делает только сам fetch.
    //   · гонка с таймером — чтобы граница держалась ДАЖЕ ЕСЛИ реализация сигнал игнорирует.
    // Второе я добавил не из вредности: первая редакция этой правки полагалась только на
    // `signal`, и тест с висящим fetch (который сигнал не слушает) провисел до упора — то есть
    // «верхняя граница» зависела от добросовестности вызывающего. Это ровно та дыра, которую
    // мы и закрываем, только на этаж ниже.
    const bounded = timeoutMs > 0 && typeof AbortController !== 'undefined';
    const ac = bounded ? new AbortController() : null;
    let timer = null;
    try {
      const call = fetchImpl(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields, isCarrier: Boolean(isCarrier) }),
        ...(ac ? { signal: ac.signal } : {}),
      });
      resp = bounded
        ? await Promise.race([
          call,
          new Promise((_, reject) => {
            timer = setTimeout(() => {
              ac.abort();
              reject(new TransportError(`extract-quotes: попытка оборвана по таймауту ${timeoutMs}ms`, { transient: true }));
            }, timeoutMs);
          }),
        ])
        : await call;
    } catch (err) {
      if (err instanceof TransportError) throw err; // наш собственный обрыв по таймауту
      // Сетевой сбой и abort — оба временные: сервис мог ответить медленно разово.
      const aborted = ac?.signal.aborted || err?.name === 'AbortError';
      throw new TransportError(
        aborted ? `extract-quotes: попытка оборвана по таймауту ${timeoutMs}ms` : (err?.message || 'network error'),
        { transient: true, cause: err },
      );
    } finally {
      // Снять обязательно: иначе таймер доживает до конца процесса и держит event loop
      // открытым даже на успешном ответе — в прогоне это выглядит как «тесты не завершаются».
      if (timer) clearTimeout(timer);
    }
    if (!resp.ok) {
      throw new TransportError(`extract-quotes: HTTP ${resp.status}`, {
        transient: classifyHttpStatus(resp.status) === 'transient',
        status: resp.status,
      });
    }
    return resp.json();
  }, retryOpts);
}
